"use client";

import { Component, ComponentType, ErrorInfo, ReactNode } from "react";
import type Rollbar from "rollbar";
import { useRollbar } from "@rollbar/react";
import { isRedirectError } from "next/dist/client/components/redirect-error";

import { getConfig } from "../../../_configs/AppConfig";
import { reportError } from "../../../utils/errorReporting";

const { publicRuntimeConfig } = getConfig();

type FallbackUI = ComponentType<{
  error: Error | null;
  resetError: () => void;
}>;

const DefaultFallbackUI: FallbackUI = ({ error, resetError }) => {
  if (error && isRedirectError(error)) {
    throw error;
  }

  if (publicRuntimeConfig.APP_ENV === "dev") {
    console.error("error: ", error);
    console.error("resetError: ", resetError);
  }
  return (
    <div className="sb-error-inside-block" style={{ padding: "1rem" }}>
      <span className="sb-error-inside-block-icon far fa-frown" />
      <div className="sb-error-inside-block-header">
        <h2>Oops, there was an error!</h2>
      </div>
      <p className="sb-error-inside-block-message">
        Don&#39;t worry, we&#39;re on it. The error has been logged and
        we&#39;ll work hard to fix it.
      </p>
    </div>
  );
};

interface Props {
  children: ReactNode;
  fallbackUI?: FallbackUI;
  userGenerated?: boolean;
  rollbar: Rollbar;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isRedirectError(error)) {
      throw error;
    }

    reportError(error, {
      error_source: "react_boundary",
      user_generated: !!this.props.userGenerated,
    });

    this.props.rollbar.error(error, {
      componentStack: info.componentStack,
      user_generated: this.props.userGenerated ? "true" : "false",
    });
  }

  render() {
    if (this.state.hasError) {
      const Fallback = this.props.fallbackUI || DefaultFallbackUI;
      return <Fallback error={this.state.error} resetError={this.resetError} />;
    }
    return this.props.children;
  }
}

export const ErrorBoundary = (
  props: Omit<Props, "rollbar">,
): React.ReactElement => {
  const rollbar = useRollbar();
  return <ErrorBoundaryInner {...props} rollbar={rollbar} />;
};
